import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject } from '@angular/core';
import { DEFAULT_PAGINATED, Environment, Identifiable, ResponseData, jsonToFormData } from '@t-llevo/util';
import {
  Observable,
  BehaviorSubject,
  Subscription,
  tap,
  take,
  map,
  catchError,
  of,
} from 'rxjs';
import { FilterParams } from '../models/filter-params';

export abstract class BaseCrudService<T extends Identifiable, R> {
  private itemListSubscription: Subscription | null = null;
  private itemSubscription: Subscription | null = null;

  protected itemListSub = new BehaviorSubject<ResponseData<T>>({
    ...DEFAULT_PAGINATED,
  });
  public itemList$ = this.itemListSub.asObservable();

  private itemSub = new BehaviorSubject<T | null>(null);
  public item$ = this.itemSub.asObservable();

  constructor(
    @Inject(Environment) public env: Environment,
    public http: HttpClient
  ) {}

  getItems(url: string, filters: FilterParams) {
    if (this.itemListSubscription) {
      this.itemListSubscription.unsubscribe();
      this.itemListSubscription = null;
    }
    const params = new HttpParams({ fromObject: filters });
    this.itemListSubscription = this.http
      .get<R>(`${this.env.apiUrl}${url}`, { params })
      .subscribe({
        next: (paginateList) => {
          this.itemListSub.next(this.transform(paginateList));
        },
        error: () => {
          this.itemListSub.next({
            count: 0,
            results: [],
            page: '',
            limit: ''
          });
        },
      });
  }

  getItemList(url: string, filters: FilterParams): Observable<ResponseData<T>> {
    const params = new HttpParams({ fromObject: filters });
    return this.http
      .get<R>(`${this.env.apiUrl}${url}`, {
        params,
      })
      .pipe(
        map((rta) => this.transform(rta)),
        tap((paginateList) => {
          this.itemListSub.next(paginateList);
        }),
        catchError(() => {
          this.itemListSub.next({
            count: 0,
            results: [],
            page: '',
            limit: ''
          });
          return of({
            count: 0,
            results: [],
            limit: '',
            page: ''
          });
        })
      );
  }

  getItem(url: string, id: number | string, fromRemote?: boolean): void {
    if (this.itemSubscription) {
      this.itemSubscription.unsubscribe();
      this.itemSubscription = null;
    }

    const item = this.itemListSub
      .getValue()
      .results.find((element) => element.id == id);
    if (item && fromRemote === false) {
      this.itemSub.next(item);
    } else {
      this.itemSubscription = this.http
        .get<T>(`${this.env.apiUrl}${url}${id}/`)
        .subscribe((itemData) => {
          this.itemSub.next(itemData);
        });
    }
  }

  createItem<K>(url: string, data: T | K) {
    return this.http.post<T>(`${this.env.apiUrl}${url}`, data, {}).pipe(
      take(1),
      tap((rsp) => {
        this._addItem(rsp);
      })
    );
  }
  createItemAsFormData<K>(url: string, data: T | K) {
    const formData = jsonToFormData(data);
    return this.http.post<T>(`${this.env.apiUrl}${url}`, formData, {}).pipe(
      take(1),
      tap((rsp) => {
        this._addItem(rsp);
      })
    );
  }

  _addItem(data: T, first?: boolean): void {
    const result = this.itemListSub.getValue();
    result.count++;
    result.results.splice(first ? 0 : -1, 0, data);
    this.itemListSub.next(result);
    this.itemSub.next(data);
  }

  updateItem<K>(url: string, id: string, data: Partial<T | K>): Observable<T> {
    return this.http.patch<T>(`${this.env.apiUrl}${url}${id}/`, data).pipe(
      take(1),
      tap((rsp) => {
        this._updateItem(rsp, id);
      })
    );
  }

  updateItemAsFormData<K>(url: string, id: string, data: T | K) {
    const formData = jsonToFormData(data);
    return this.http
      .patch<T>(`${this.env.apiUrl}${url}${id}/`, formData, {})
      .pipe(
        take(1),
        tap((rsp) => {
          this._updateItem(rsp, id);
        })
      );
  }

  _updateItem(rsp: T, id: string) {
    const result = this.itemListSub.getValue();
    const index = result.results.findIndex((val) => val.id == id);
    result.results[index] = rsp;
    this.itemListSub.next(result);
    this.itemSub.next(rsp);
  }

  deleteItem(url: string, id: string) {
    return this.http.delete(`${this.env.apiUrl}${url}${id}/`).pipe(
      take(1),
      tap(() => {
        const result = this.itemListSub.getValue();
        const index = result.results.findIndex((val) => val.id === id);
        result.count--;
        result.results.splice(index, 1);
        this.itemListSub.next(result);
      })
    );
  }

  clearActiveItem(): void {
    this.itemSub.next(null);
  }

  abstract transform(data: R): ResponseData<T>;
}
